import { displayError, getApiUrl, getCurrentLanguageId, loadAsyncComponent, QueryParameter } from "./common";
import { loadScript } from "@paypal/paypal-js";
import LoadingIndication from "./loading-indication";

export default class PayPalPayment {
    private baseElement: HTMLElement;
    private orderGuid: string;
    private clientId: string;
    private currency: string;
    private parentElement: HTMLElement;

    private static _componentName = 'PayPalPayment';
    private static buttonName = '#paypal-button-container';

    static get componentName() {
        return PayPalPayment._componentName;
    }

    static get ButtonContainerName() {
        return PayPalPayment.buttonName;
    }

    constructor() {
        this.baseElement = document.querySelector(`[data-component="${PayPalPayment.componentName}"]`);
        this.orderGuid = this.baseElement.dataset.orderGuid;
        this.clientId = this.baseElement.dataset.clientId;
        this.currency = this.baseElement.dataset.currencyCode;
        this.parentElement = document.querySelector(`[data-component="${this.baseElement.dataset.parentComponentName}"]`);
    }

    public async initButton(): Promise<void> {
        this.startLoading();

        const paypal = await loadScript({
            'client-id': this.clientId,
            'currency': this.currency
        });

        await paypal.Buttons({
            style: {
                layout: 'horizontal',
                shape: 'rect',
                color: 'gold',
                height: 25,
                label: 'pay',
                tagline: false
            },
            createOrder: async () => await this.createPayPalOrder(this.orderGuid),
            onApprove: async (data, actions) => await this.onPayPalApproveOrder(data, actions),
            onError: async (err) => function () {
                displayError("Unexpected PayPal payment gateway error")
            },
        }).render(PayPalPayment.ButtonContainerName);
        this.stopLoading();
    }

    private async createPayPalOrder(orderGuid: string): Promise<any>{
        const createOrderUrl = `${getApiUrl()}/paypal/createorder`;
        const requestJson = JSON.stringify({
            orderGuid: orderGuid,
            returnUrl: window.location.href
        });

        const response = await fetch(createOrderUrl, {
            method: 'POST',
            body: requestJson,
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': getCurrentLanguageId()
            }
        });

        if (!response.ok) {
            displayError("PayPal payment creation failed. Please try it later.");
            return null;
        }

        const data = await response.json();
        const responseJson = JSON.parse(data);
        return responseJson.id;
    }

    private async onPayPalApproveOrder(data, actions): Promise<void>{
        const approvePaymentUrl = `${getApiUrl()}/paypal/capturepayment`;
        const requestJson = JSON.stringify({
            orderID: data.orderID
        });

        LoadingIndication.show(this.parentElement);
        const response = await fetch(approvePaymentUrl, {
            method: 'POST',
            body: requestJson,
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': getCurrentLanguageId()
            }
        });

        if (!response.ok) {
            displayError("PayPal payment capture failed.");
            return null;
        }

        const responseData = await response.json();
        const responseJson = JSON.parse(responseData);

        if (responseJson.PaymentCaptureSuccessful) {
            const queryParam: QueryParameter = {
                key: 'pay-res',
                value: 'Success'
            };

            this.parentElement = await loadAsyncComponent(this.parentElement, [queryParam]);
            LoadingIndication.hide(this.parentElement);
        } else {
            this.parentElement = await loadAsyncComponent(this.parentElement);
            LoadingIndication.hide(this.parentElement);
        }
    }

    private startLoading() {
        LoadingIndication.show(this.baseElement);
    }

    private stopLoading() {
        LoadingIndication.hide(this.baseElement);
    }
}
